import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useDebounce } from "@/react/hooks/useDebounce.js";

const SpotIncentiveDateRangePicker = ({
  timelineStartDate,
  timelineEndDate,
  selectedStartDate,
  selectedEndDate,
  onChange
}) => {
  // Cache timeline calculations - work with day boundaries only
  const timelineInfo = useMemo(() => {
    // Get start of day for timeline boundaries
    const timelineStartDay = new Date(timelineStartDate);
    timelineStartDay.setHours(0, 0, 0, 0);

    const timelineEndDay = new Date(timelineEndDate);
    timelineEndDay.setHours(0, 0, 0, 0);

    const totalMs = timelineEndDay.getTime() - timelineStartDay.getTime();
    const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
    /* Minimum drag value should be set to one day */
    const minStepSize = 1 / totalDays; // 1 day as fraction of total range

    return {
      totalMs,
      totalDays,
      minStepSize,
      timelineStartDay,
      timelineEndDay
    };
  }, [timelineStartDate, timelineEndDate]);

  // Convert selected dates to day-based positions (0-1 range) with caching
  const initialPositions = useMemo(() => {
    let startPos = 0;
    let endPos = 1;

    if (selectedStartDate) {
      // Get the day difference from timeline start
      const selectedStartDay = new Date(selectedStartDate);
      selectedStartDay.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((selectedStartDay.getTime() - timelineInfo.timelineStartDay.getTime()) / (1000 * 60 * 60 * 24));
      startPos = Math.max(0, Math.min(1, daysDiff / timelineInfo.totalDays));
    }

    if (selectedEndDate) {
      // Get the day difference from timeline start
      const selectedEndDay = new Date(selectedEndDate);
      selectedEndDay.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((selectedEndDay.getTime() - timelineInfo.timelineStartDay.getTime()) / (1000 * 60 * 60 * 24));
      endPos = Math.max(0, Math.min(1, daysDiff / timelineInfo.totalDays));
    }

    return { startPos, endPos };
  }, [selectedStartDate, selectedEndDate, timelineInfo.timelineStartDay, timelineInfo.totalDays]);

  // State for shade positions (0-1 range) - immediate updates for UI
  const [leftShadePosition, setLeftShadePosition] = useState(initialPositions.startPos);
  const [rightShadePosition, setRightShadePosition] = useState(initialPositions.endPos);

  // Reduced debounce delay for faster response (50ms instead of 100ms)
  const debouncedLeftPosition = useDebounce(leftShadePosition, 50);
  const debouncedRightPosition = useDebounce(rightShadePosition, 50);

  // Dragging states
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);

  const timelineRef = useRef(null);
  const isInternalUpdateRef = useRef(false);

  // Store normalized time values - start dates at 00:00:00, end dates at 23:59:59
  const normalizedTimeValues = useMemo(() => {
    return {
      startTime: {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
      },
      endTime: {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
      }
    };
  }, []); // Static values, no dependencies needed

  // Calculate current selected dates based on day positions with normalized times
  const currentDates = useMemo(() => {
    // Calculate day offsets from positions
    const startDayOffset = Math.floor(leftShadePosition * timelineInfo.totalDays);
    const endDayOffset = Math.floor(rightShadePosition * timelineInfo.totalDays);

    // Create new dates by adding day offsets to timeline start
    const startDate = new Date(timelineInfo.timelineStartDay);
    startDate.setDate(startDate.getDate() + startDayOffset);

    const endDate = new Date(timelineInfo.timelineStartDay);
    endDate.setDate(endDate.getDate() + endDayOffset);

    // Apply normalized time values: start at 00:00:00, end at 23:59:59
    startDate.setHours(
      normalizedTimeValues.startTime.hours,
      normalizedTimeValues.startTime.minutes,
      normalizedTimeValues.startTime.seconds,
      normalizedTimeValues.startTime.milliseconds
    );

    endDate.setHours(
      normalizedTimeValues.endTime.hours,
      normalizedTimeValues.endTime.minutes,
      normalizedTimeValues.endTime.seconds,
      normalizedTimeValues.endTime.milliseconds
    );

    return { startDate, endDate };
  }, [leftShadePosition, rightShadePosition, timelineInfo.timelineStartDay, timelineInfo.totalDays, normalizedTimeValues]);

  // Trigger onChange when debounced positions change with normalized time values
  useEffect(() => {
    // Calculate day offsets from debounced positions
    const startDayOffset = Math.floor(debouncedLeftPosition * timelineInfo.totalDays);
    const endDayOffset = Math.floor(debouncedRightPosition * timelineInfo.totalDays);

    // Create new dates by adding day offsets to timeline start
    const debouncedStartDate = new Date(timelineInfo.timelineStartDay);
    debouncedStartDate.setDate(debouncedStartDate.getDate() + startDayOffset);

    const debouncedEndDate = new Date(timelineInfo.timelineStartDay);
    debouncedEndDate.setDate(debouncedEndDate.getDate() + endDayOffset);

    // Apply normalized time values: start at 00:00:00, end at 23:59:59
    debouncedStartDate.setHours(
      normalizedTimeValues.startTime.hours,
      normalizedTimeValues.startTime.minutes,
      normalizedTimeValues.startTime.seconds,
      normalizedTimeValues.startTime.milliseconds
    );

    debouncedEndDate.setHours(
      normalizedTimeValues.endTime.hours,
      normalizedTimeValues.endTime.minutes,
      normalizedTimeValues.endTime.seconds,
      normalizedTimeValues.endTime.milliseconds
    );

    // Mark as internal update and trigger onChange
    isInternalUpdateRef.current = true;
    onChange?.(debouncedStartDate, debouncedEndDate);
    // Reset flag after a microtask to allow the parent to update
    Promise.resolve().then(() => {
      isInternalUpdateRef.current = false;
    });
  }, [debouncedLeftPosition, debouncedRightPosition, timelineInfo.timelineStartDay, timelineInfo.totalDays, normalizedTimeValues, onChange]);

  // Generate timeline markers based on day boundaries (memoized for performance)
  const markers = useMemo(() => {
    const markerList = [];
    const numMarkers = 9; // Create 9 evenly spaced markers

    for (let i = 0; i < numMarkers; i++) {
      const position = i / (numMarkers - 1); // 0, 0.125, 0.25, ..., 1
      const dayOffset = Math.floor(position * timelineInfo.totalDays);
      const markerDate = new Date(timelineInfo.timelineStartDay);
      markerDate.setDate(markerDate.getDate() + dayOffset);

      markerList.push({
        date: markerDate,
        position: position * 100,
        label: markerDate.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric"
        })
      });
    }

    return markerList;
  }, [timelineInfo.timelineStartDay, timelineInfo.totalDays]);

  const getPositionFromEvent = useCallback(e => {
    if (!timelineRef.current) return 0;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const rawPosition = Math.max(0, Math.min(1, x / rect.width));

    // Snap to day boundaries
    const dayPosition = Math.round(rawPosition * timelineInfo.totalDays) / timelineInfo.totalDays;
    return Math.max(0, Math.min(1, dayPosition));
  }, [timelineInfo.totalDays]);

  const handleMouseDown = type => e => {
    e.preventDefault();
    if (type === "left") {
      setIsDraggingLeft(true);
    } else {
      setIsDraggingRight(true);
    }
  };

  const handleMouseMove = useCallback(
    e => {
      if (!isDraggingLeft && !isDraggingRight) return;

      const position = getPositionFromEvent(e);

      if (isDraggingLeft) {
        // Ensure at least 1 day gap between handles
        const maxPosition = Math.min(rightShadePosition - timelineInfo.minStepSize, 1);
        setLeftShadePosition(Math.min(position, maxPosition));
      } else if (isDraggingRight) {
        // Ensure at least 1 day gap between handles
        const minPosition = Math.max(leftShadePosition + timelineInfo.minStepSize, 0);
        setRightShadePosition(Math.max(position, minPosition));
      }
    },
    [
      isDraggingLeft,
      isDraggingRight,
      rightShadePosition,
      leftShadePosition,
      getPositionFromEvent,
      timelineInfo.minStepSize
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDraggingLeft(false);
    setIsDraggingRight(false);
  }, []);

  useEffect(() => {
    if (isDraggingLeft || isDraggingRight) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDraggingLeft, isDraggingRight, handleMouseMove, handleMouseUp]);

  // Update positions when external selectedStartDate/selectedEndDate props change (combined for efficiency)
  useEffect(() => {
    // Only update positions if this isn't from our own internal update
    if (!isInternalUpdateRef.current) {
      setLeftShadePosition(initialPositions.startPos);
      setRightShadePosition(initialPositions.endPos);
    }
  }, [initialPositions.startPos, initialPositions.endPos]);

  return (
    <div className="space-y-swivu-base">
      <div className="text-center">
        <div className="flex items-center justify-center gap-swivu-horizontal text-swivu-md font-bold text-foreground">
          <span>
            {currentDates.startDate.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric"
            })}{" "}
            —{" "}
            {currentDates.endDate.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric"
            })}
          </span>
          <span className="text-base text-muted-foreground font-medium">
            (
            {Math.ceil(
              (currentDates.endDate.getTime() - currentDates.startDate.getTime()) /
                (1000 * 60 * 60 * 24)
            )}{" "}
            days)
          </span>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="relative px-8">
        {/* Timeline Track */}
        <div
          ref={timelineRef}
          className="relative h-8 bg-secondary rounded-full cursor-pointer select-none shadow-inner"
        >
          {/* Left Shade */}
          <div
            className={`absolute top-0 left-0 h-full bg-muted/50 rounded-l-full ${
              isDraggingLeft || isDraggingRight ? 'transition-none' : 'transition-all duration-75 ease-out'
            }`}
            style={{ width: `${leftShadePosition * 100}%` }}
          />

          {/* Right Shade */}
          <div
            className={`absolute top-0 right-0 h-full bg-muted/50 rounded-r-full ${
              isDraggingLeft || isDraggingRight ? 'transition-none' : 'transition-all duration-75 ease-out'
            }`}
            style={{ width: `${(1 - rightShadePosition) * 100}%` }}
          />

          {/* Selected Range */}
          <div
            className={`absolute top-0 h-full bg-gray-600 ${
              isDraggingLeft || isDraggingRight ? 'transition-none' : 'transition-all duration-75 ease-out'
            }`}
            style={{
              left: `${leftShadePosition * 100}%`,
              width: `${(rightShadePosition - leftShadePosition) * 100}%`
            }}
          />

          {/* Left Handle */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-8 bg-white rounded-lg cursor-ew-resize shadow-lg border-2 ${
              isDraggingLeft
                ? "scale-110 shadow-xl transition-none"
                : "hover:scale-105 hover:shadow-xl transition-all duration-100 ease-out"
            }`}
            style={{
              left: `calc(${leftShadePosition * 100}% - 10px)`,
              transform: isDraggingLeft
                ? 'translateY(-50%) scale(1.1)'
                : 'translateY(-50%)',
              borderColor: '#68758a'
            }}
            onMouseDown={handleMouseDown("left")}
          >
            <div className="absolute inset-0 rounded-md flex items-center justify-center" style={{ backgroundColor: '#68758a' }}>
              <div className="w-0.5 h-3 bg-white rounded-full opacity-80" />
            </div>
          </div>

          {/* Right Handle */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-8 bg-white rounded-lg cursor-ew-resize shadow-lg border-2 ${
              isDraggingRight
                ? "scale-110 shadow-xl transition-none"
                : "hover:scale-105 hover:shadow-xl transition-all duration-100 ease-out"
            }`}
            style={{
              left: `calc(${rightShadePosition * 100}% - 10px)`,
              transform: isDraggingRight
                ? 'translateY(-50%) scale(1.1)'
                : 'translateY(-50%)',
              borderColor: '#68758a'
            }}
            onMouseDown={handleMouseDown("right")}
          >
            <div className="absolute inset-0 rounded-md flex items-center justify-center" style={{ backgroundColor: '#68758a' }}>
              <div className="w-0.5 h-3 bg-white rounded-full opacity-80" />
            </div>
          </div>
        </div>

        {/* Timeline Markers */}
        <div className="relative mt-3 pb-6">
          {markers.map((marker, index) => (
            <div
              key={index}
              className="absolute flex flex-col items-center -translate-x-1/2"
              style={{ left: `${marker.position}%` }}
            >
              <div className="w-px h-2 bg-border" />
              <div className="text-swivu-sm text-muted-foreground whitespace-nowrap text-center">
                {marker.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpotIncentiveDateRangePicker;
